import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useGetProjectDetailsQuery } from "api/api";
import { Project } from 'types/types';
import { calculateEmbodiedCarbon, calculateCarbonIntensity, SmqCarbonSubtotals } from 'lib/utils/carbonTotals';

interface ProjectContextProps {
    projectDetails: Project;
    setProjectDetails: React.Dispatch<React.SetStateAction<Project>>;
    setProjectId: (projectId: number | null) => void;
    projectId: number | null;
    smqSubtotals: SmqCarbonSubtotals;
    setSmqSubtotals: React.Dispatch<React.SetStateAction<SmqCarbonSubtotals>>;
    isNewProject: boolean;
    setIsNewProject: (isNewProject: boolean) => void;
}

const defaultEmptyProject: Project = {
    id: -1,
    primary_building_use_type: null,
    construction_type: null,
    risk_category: null,
    seismic_design_category: null,
    primary_horizontal_gravity_system: null,
    primary_vertical_gravity_system: null,
    primary_lateral_system: null,
    podium: null,
    foundation_type: null,
    material_quantity_data_source: null,
    carbon_factor_data_source: null,
    lca_tool: null,
    lca_project_phase: null,
    created_by: null,
    created_at: null,
    updated_at: null,
    submitted_at: null,
    is_submitted: false,
    name: null,
    address: null,
    country: null,
    subnational: null,
    city: null,
    zipcode: null,
    construction_completion_year: null,
    gross_area_sqft: null,
    mean_roof_height_ft: null,
    stories_above_grade: null,
    stories_below_grade: null,
    tcg_long_direction_ft: null,
    typical_floor_live_load_psf: null,
    ground_snow_load_psf: null,
    ultimate_wind_speed_mph: null,
    allowable_soil_bearing_pressure_psf: null,
    steel_connection_allowances: null,
    rebar_allowances: null,
    concrete_waste_allowances: null,
    other_carbon_factor_data_source: null,
    additional_assumptions: null,
    other_lca_tool: null,
    lca_year: null,
    ref_study_years: null,
    a1_a3_gwp_kg: null,
    a4_gwp_kg: null,
    a5_gwp_kg: null,
    b1_b5_gwp_kg: null,
    c1_c4_gwp_kg: null,
    d_gwp_kg: null,
    biogenic_carbon_flow_kg: null,
    biogenic_carbon_content: null,
    updated_by: null,
    submitted_by: null,
    total_intensity_exclude_d: null,
    total_gwp_kg_exclude_d: null,
    ECOM_version: null,
    smqs: []
};

const defaultSmqSubtotals: SmqCarbonSubtotals = {
    Substructure: 0,
    Superstructure: 0,
};

export const ProjectContext = createContext<ProjectContextProps | undefined>(undefined);

interface ProjectProviderProps {
    children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
    const [projectId, setProjectId] = useState<number | null>(null);
    const { data: projectDataFromApi, refetch } = useGetProjectDetailsQuery(projectId!, { skip: projectId === null || projectId === -1 });
    const [projectDetails, setProjectDetails] = useState<Project>(defaultEmptyProject);
    const [smqSubtotals, setSmqSubtotals] = useState<SmqCarbonSubtotals>(defaultSmqSubtotals);
    const [isNewProject, setIsNewProject] = useState<boolean>(false);

    const idProjectMatch = (projectId ?? -1) !== -1 && projectDataFromApi && projectId === projectDetails.id;

    useEffect(() => {
        if (projectId === -1) {
            setProjectDetails(defaultEmptyProject);
            setSmqSubtotals(defaultSmqSubtotals);
        } else if (projectDataFromApi) {
            setProjectDetails(projectDataFromApi);
        }
    }, [projectId, projectDataFromApi]);

    useEffect(() => {
        const totalEmbodiedCarbon = calculateEmbodiedCarbon(projectDetails, smqSubtotals);
        const carbonIntensity = calculateCarbonIntensity(totalEmbodiedCarbon, projectDetails.gross_area_sqft);

        setProjectDetails(prevDetails => ({
            ...prevDetails,
            total_gwp_kg_exclude_d: totalEmbodiedCarbon,
            total_intensity_exclude_d: carbonIntensity
        }));
    }, [
        projectDetails.a1_a3_gwp_kg,
        projectDetails.a4_gwp_kg,
        projectDetails.a5_gwp_kg,
        projectDetails.b1_b5_gwp_kg,
        projectDetails.c1_c4_gwp_kg,
        projectDetails.gross_area_sqft,
        smqSubtotals
    ]);

    return (
        <ProjectContext.Provider value={{
                                            projectId,
                                            projectDetails: idProjectMatch ? projectDetails : defaultEmptyProject,
                                            setProjectDetails,
                                            setProjectId,
                                            smqSubtotals: idProjectMatch ? smqSubtotals : defaultSmqSubtotals,
                                            setSmqSubtotals,
                                            isNewProject,
                                            setIsNewProject
                                        }}>
            {children}
        </ProjectContext.Provider>
    );
};